// PatientList.tsx
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import "../styles/patientList.css";
import { SERVER_URL } from "../config";

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  region: string;
  user: { email: string };
}

const LIMIT = 20;

const PatientList: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearch] = useState("");

  /* -------- fetch current page -------- */
  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetchWithAuth(
        `${SERVER_URL}/api/patient/patients?page=${page}&limit=${LIMIT}&q=${encodeURIComponent(
          searchTerm
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch patients");

      const data: Patient[] = await res.json();
      setPatients(data);
      setHasNext(data.length === LIMIT); // if fewer than LIMIT → no next page
    } catch {
      setError("Failed to fetch patient data.");
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm]);

  /* fetch when page or search changes */
  useEffect(() => {
    loadPage();
  }, [loadPage]);

  /* reset page when search term changes */
  const handleSearch = (term: string) => {
    setSearch(term);
    setPage(1);
  };

  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="patient-list-container">
      <h2>Patient List</h2>

      <input
        className="search-input"
        placeholder="Search by name, email, or state"
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
      />

      {patients.length === 0 && !loading ? (
        <p className="no-results">No patients found.</p>
      ) : (
        <ul className="patient-list">
          {patients.map((p) => (
            <li key={p.id} className="patient-item">
              <Link to={`/patient-profile/${p.id}`} className="patient-link">
                <strong>
                  {p.firstName} {p.lastName}
                </strong>{" "}
                – {p.region} – {p.user.email}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* pagination controls */}
      <div className="pagination-bar">
        <button
          className="page-btn"
          disabled={page === 1 || loading}
          onClick={() => setPage((prev) => prev - 1)}
        >
          ‹ Prev
        </button>

        <span className="page-number">Page {page}</span>

        <button
          className="page-btn"
          disabled={!hasNext || loading}
          onClick={() => setPage((prev) => prev + 1)}
        >
          Next ›
        </button>
      </div>

      {loading && <p className="loading-text">Loading…</p>}
    </div>
  );
};

export default PatientList;
